import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';
import {debounce} from 'ts-debounce';

import * as analytics from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import {AlignmentPolymerType, RESIDUE_NAMES} from 'constants/AlignmentConstants';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {CDRDefinition} from 'molbio/antibody-engineering/AntibodyEngineeringConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Label from 'ui-library/bs/Label';
import {ButtonSize} from 'ui-library/buttons/Button';
import FieldError from 'ui-library/FieldError';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import Spinner from 'ui-library/Spinner';
import ToggleGroup from 'ui-library/ToggleGroup';
import {capitalize} from 'util/NameUtil';
import {InputChangeEvent} from 'util/Types';

import {
  LANDSCAPE,
  LETTER,
  PageSize,
  PDFExportRawSequences,
} from './AlignmentPDFHelpers/AlignmentPDFConstants';
import generateAlignmentPDF, {getDefaultResiduesPerLine} from './AlignmentPDFHelpers/generateAlignmentPDF';

export interface AlignmentPDFDownloadModalProps {
  alignmentName: string;
  alignmentPolymerType: AlignmentPolymerType;
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  rawAlignmentData: PDFExportRawSequences;
  selection: {start: number; end: number | null};
  cdrDefinition?: CDRDefinition;
}

export const DEBOUNCE_MS = 250;

const debouncedGenerateAlignmentPDF = debounce(generateAlignmentPDF, DEBOUNCE_MS);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const AlignmentPDFDownloadModal = (props: AlignmentPDFDownloadModalProps) => {
  const [blobURL, setBlobUrl] = useState('');
  const [showMismatches, setShowMismatches] = useState(false);
  const [pageFormat, setPageFormat] = useState<PageSize>(LETTER);
  const [residuesPerLine, setResiduesPerLine] = useState(getDefaultResiduesPerLine(pageFormat));
  const [residuesError, setResiduesError] = useState<string | null>(null);
  const [useSelection, setUseSelection] = useState(false);

  const hasCDRAnnotations = props.rawAlignmentData.some((s) => Boolean(s.cdrAnnotations?.length));
  const [useCDRAnnotations, setUseCDRAnnotations] = useState(hasCDRAnnotations);

  const residuesName = pluralize(2, RESIDUE_NAMES[props.alignmentPolymerType]);
  const capitalizedResiduesName = capitalize(residuesName);
  const minResidues = 10;
  const maxResidues = getDefaultResiduesPerLine(pageFormat);

  useEffect(() => {
    setResiduesPerLine(getDefaultResiduesPerLine(pageFormat));
  }, [pageFormat]);

  useEffect(() => {
    if (!residuesError) {
      // Reset PDF preview while new one is recalculating
      setBlobUrl('');
      debouncedGenerateAlignmentPDF(
        useCDRAnnotations
          ? props.rawAlignmentData
          : props.rawAlignmentData.map((row) => ({...row, cdrAnnotations: undefined})),
        props.alignmentName,
        props.cdrDefinition ?? null,
        {
          showMismatches,
          residuesPerLine,
          pageSize: pageFormat,
          selectionStart: useSelection ? props.selection.start : 0,
          selectionEnd: useSelection ? props.selection.end : null,
        },
        (url: string) => {
          setBlobUrl(url);
        }
      );
    }
  }, [
    props.rawAlignmentData,
    showMismatches,
    residuesPerLine,
    residuesError,
    pageFormat,
    useSelection,
    props.selection,
    props.alignmentName,
    useCDRAnnotations,
    props.cdrDefinition,
  ]);

  function onSubmit(): void {
    analytics.deprecatedTrackEvent(MOLBIO_ANALYTICS_ACTIONS.DOWNLOADED_FORMATTED_ALIGNMENT_PDF, {
      residueType: props.alignmentPolymerType,
      showMismatches,
      residuesPerLine,
      pageFormat,
      useSelection,
      useCDRAnnotations,
    });
    window.open(blobURL, '_blank');
    props.onRequestSubmit();
  }

  return (
    <Modal
      buttons={[
        {
          text: blobURL ? 'Download' : 'Generating PDF...',
          submit: true,
          disabled: !blobURL,
          dataTestAttrs: {component: 'AlignmentPDFDownloadModal', element: 'downloadButton'},
        },
      ]}
      onRequestSubmit={onSubmit}
      onRequestClose={props.onRequestClose}
      title="Export Alignment as PDF"
      dataTestAttrs={{component: 'AlignmentPDFDownloadModal', element: 'root'}}
    >
      <div>
        <div className={css(styles.form)}>
          <div className={css(styles.formRow)}>
            <FormGroup className={css(styles.formGroup)}>
              <Label>Page Layout</Label>
              <ToggleGroup
                selection={pageFormat === LETTER ? 'letter' : 'landscape'}
                size={ButtonSize.SMALL}
                onChangeSelection={(format: string) => {
                  setPageFormat(format === 'letter' ? LETTER : LANDSCAPE);
                }}
                options={[
                  {id: 'letter', buttonProps: {text: 'Letter'}},
                  {id: 'landscape', buttonProps: {text: 'Landscape'}},
                ]}
              />
            </FormGroup>
            <FormGroup className={css(styles.formGroup, styles.rightColumn)}>
              <Label>Highlight mismatches and unknown {residuesName}</Label>
              <FormInput
                type="checkbox"
                name="showMismatches"
                className={css(styles.checkbox)}
                checked={showMismatches}
                onChange={(e: InputChangeEvent) => {
                  setShowMismatches(e.target.checked);
                }}
              />
            </FormGroup>
          </div>
          <div className={css(styles.formRow)}>
            <FormGroup className={css(styles.formGroup)}>
              <Label>{capitalize(residuesName)} per line</Label>
              <FormInput
                type="number"
                className={css(styles.residuesInput)}
                name={`${capitalizedResiduesName} per line`}
                focusOnMount={false}
                value={residuesPerLine.toString()}
                onChange={(e: InputChangeEvent) => {
                  const num = parseInt(e.target.value, 10);
                  setResiduesPerLine(num);
                  if (num >= minResidues && num <= maxResidues) {
                    setResiduesError(null);
                  } else {
                    setResiduesError(
                      `${capitalizedResiduesName} must be a number between ${minResidues} and ${maxResidues}`
                    );
                  }
                }}
              />
              <FieldError message={residuesError} />
            </FormGroup>
            <FormGroup className={css(styles.formGroup, styles.rightColumn)}>
              <Label>Use selected {residuesName}</Label>
              <FormInput
                type="checkbox"
                name="useSelection"
                className={css(styles.checkbox)}
                checked={useSelection}
                onChange={(e: InputChangeEvent) => {
                  setUseSelection(e.target.checked);
                }}
              />
            </FormGroup>
            {hasCDRAnnotations && (
              <FormGroup className={css(styles.formGroup, styles.rightColumn)}>
                <Label>Use CDR Annotations</Label>
                <FormInput
                  type="checkbox"
                  name="useCDRAnnotations"
                  className={css(styles.checkbox)}
                  checked={useCDRAnnotations}
                  onChange={(e: InputChangeEvent) => {
                    setUseCDRAnnotations(e.target.checked);
                  }}
                />
              </FormGroup>
            )}
          </div>
          <div className={css(styles.formRow)}>
            <p>
              {`By default, we show up to 12 characters of sequence names. If names are ellipsized and you want to see more name characters, adjust '${residuesName} per line' down.`}
            </p>
          </div>
          <div className={css(styles.formRow)}>
            <p>
              <BenchlingIcon icon="warning" className="u-warning-color u-tiny-spacing-right" />
              If you are having trouble downloading, please check if any browser extensions prevent popups.
            </p>
          </div>
        </div>
        <div className={classNames(css(styles.wrapper), css(styles.centered))}>
          {blobURL ? (
            <iframe className={css(styles.iframe)} title="Alignment PDF Preview" src={blobURL} />
          ) : (
            <div className={css(styles.centered)}>
              Generating PDF preview; please do not refresh the page.
              <Spinner />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AlignmentPDFDownloadModal;

const styles = StyleSheet.create({
  centered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  wrapper: {
    height: '470px',
  },
  iframe: {
    height: '500px',
    width: '460px', // keep it wide enough to see the download/print/rotate buttons in the preview
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  formRow: {
    marginLeft: '40px',
    display: 'flex',
    alignItems: 'center',
  },
  rightColumn: {
    marginLeft: '50px',
  },
  residuesInput: {
    width: '50%',
  },
  checkbox: {
    width: 'inherit',
    margin: 0,
    height: '20px',
  },
  dropdown: {
    overflow: 'scroll',
  },
  form: {
    marginBottom: '20px',
  },
});
